/**
 * Imports
 *
 * Import any modules or components here.
 */
import * as Sentry from "@sentry/browser";
import { i18n, outputDiv } from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
import { modal } from "commonAssets/lib/modal.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import "./error.css";

// Initialize Sentry for error tracking
Sentry.init({
    dsn: process.env.sentryDsn,
    // Add optional integrations for additional features
    integrations: [Sentry.replayIntegration()],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    // Set the environment
    environment: process.env.mode,

    // Before sending an event, check if the environment is production
    beforeSend(event, hint) {
        // Don't send events in development
        if (process.env.mode === "development") {
            return null;
        }

        // Ignore timeout errors
        if (hint.originalException === "Timeout") {
            return null;
        }

        return event;
    },
});

/**
 * ErrorHandler
 *
 * Usage:
 *
 * ```javascript
 * const errorHandler = new ErrorHandler();
 * errorHandler.display(error);
 * ```
 *
 * Assumption:
 *
 * The error object should have a status and message property.
 *
 * Example:
 *
 * ```json
 * {
 *   "status": 403,
 *   "message": "This is a test error message using the key of `message`"
 * }
 * ```
 *
 * Or:
 *
 * ```json
 * {
 *   "data": {
 *       "status": 403,
 *       "message": "This is a test error message using the key of `message`"
 *   },
 *   "status": 403,
 *   "statusText": "",
 *   ...
 * }
 * ```
 *
 * @class
 * @since 3.0.0
 */
export class ErrorHandler {
    /**
     * Displays the error message on the screen and throws an Error.
     *
     * @param {Error} error - The error object.
     * @returns {void}
     */
    display(error) {
        const status = this.get_error_status(error);
        const message = this.get_error_message(error);
        const errorHtml = (status, message) => {
            return `
                ${status} <br> 
                ${message} <br> 
                ${i18n.getTranslation("return_to_home")}
            `;
        };
        const errorDiv = createElement(
            "div",
            {
                class: "text-center text-danger fw-bold fs-4 py-5 mt-5",
                "aria-label": this.get_error_message(error),
            },
            [],
            errorHtml(status, message)
        );

        outputDiv.innerHTML = ``;
        outputDiv.appendChild(errorDiv);
        this.hideLoadingGif();
    }

    /**
     * Displays a modal with the given error information.
     *
     * @param {Error} error - The error object.
     * @returns {void}
     */
    display_modal(error) {
        const status = this.get_error_status(error);
        const message = this.get_error_message(error);

        modal(status, message);
        this.hideLoadingGif();
    }

    /**
     * Displays the OTP modal based on the error status.
     * @param {Object} error - The error object containing the status.
     */
    display_otp_modal(error) {
        switch (error.status) {
            case 401:
                modal(
                    i18n.getTranslation("error"),
                    i18n.getTranslation("otp_invalid_error")
                );
                break;
            default:
                modal(
                    i18n.getTranslation("error"),
                    i18n.getTranslation("claim_processing_error")
                );
        }
        document.getElementById("submit-otp").disabled = false;
        this.hideLoadingGif();
    }

    /**
     * Returns the error status message based on the provided error object.
     * @param {Object} error - The error object.
     * @returns {string} - The error status message.
     */
    get_error_status(error) {
        if (typeof error === "object" && error.hasOwnProperty("status")) {
            if (isVpnCorsError(error)) {
                return `⚠️ ${i18n.getTranslation("access_issue_detected")}`;
            }
            if (error.status >= 400 && error.status < 500) {
                return `${i18n.getTranslation("error")} ${error.status}`;
            }
            if (error.status >= 500) {
                // return `${i18n.getTranslation('error')} ${error.status}`;
                return `${i18n.getTranslation("error")}`;
            }
        }
        return i18n.getTranslation("error");
    }

    /**
     * Retrieves the error message from the given error object.
     *
     * @param {Error} error - The error object.
     * @returns {string} The error message.
     */
    get_error_message(error) {
        return isVpnCorsError(error)
            ? i18n.getTranslation("vpn_cors_error")
            : this.find_key(error, "message");
    }

    /**
     * Finds a key in an object recursively.
     *
     * @param {Object} obj - The object to search in.
     * @param {string} key - The key to find.
     * @returns {any} - The value associated with the key, or "There was an error." if the key is not found.
     */
    find_key(obj, key) {
        if (obj === null || typeof obj !== "object") {
            return i18n.getTranslation("claim_processing_error");
        }

        for (const k in obj) {
            if (k === key) {
                return obj[k];
            }
            if (obj[k] !== null && typeof obj[k] === "object") {
                const result = this.find_key(obj[k], key);
                if (result !== undefined) {
                    return result;
                }
            }
        }

        if (obj.hasOwnProperty("error")) {
            return obj.error;
        }

        return i18n.getTranslation("claim_processing_error");
    }

    /**
     * Hides the loading GIF if it is currently being displayed.
     */
    hideLoadingGif() {
        if (document.querySelector("#loading").classList.contains("show")) {
            loadingGif(loading, "hide", 250);
        }
    }

    /**
     * Logs an exception using Sentry.
     * @param {Error} error - The error object.
     */
    log_exception(error) {
        if (process.env.mode === "development") {
            console.error(error);
        } else {
            Sentry.captureException(error);
        }
    }

    /**
     * Sets the user context for Sentry.
     * @param {Object} user - The user object containing id and email.
     */
    set_user({ id, email }) {
        Sentry.setUser({
            id,
            email,
        });
    }
}

function isVpnCorsError(error) {
    return error.status === 0;
}
